// Phase 25 iter-8 — Inbox-style messages list. Two-pane:
//   • Left: lista de conversas (auto-polleadas).
//   • Right: detalle da conversa seleccionada (route /marketplace/messages/:id).
//
// Polling cada 15 s mentres a páxina está aberta para refrescar tanto a
// lista coma a conversa abertas; non hai WebSocket.

import { MessagesSquare } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, NavLink, Outlet, useParams } from 'react-router-dom';

import type { ConversationListItem } from '@leyrochain/shared';

import { listConversations } from '../api/conversations';
import { describeAxiosError } from '../api/errors';
import Avatar from '../components/ui/Avatar';
import Spinner from '../components/ui/Spinner';
import { useAuthContext } from '../context/AuthContext';

const POLL_INTERVAL_MS = 15_000;

export default function MessagesPage(): JSX.Element {
  const { accessToken } = useAuthContext();
  const [items, setItems] = useState<ConversationListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const params = useParams<{ id?: string }>();

  const refresh = useCallback(async () => {
    try {
      const res = await listConversations();
      setItems(res.conversations);
      setError(null);
    } catch (err) {
      setError(describeAxiosError(err, 'Non se puido cargar a túa caixa.'));
    }
  }, []);

  useEffect(() => {
    if (!accessToken) return;
    void refresh();
    const t = window.setInterval(() => void refresh(), POLL_INTERVAL_MS);
    return () => window.clearInterval(t);
  }, [accessToken, refresh]);

  if (!accessToken) {
    return <Navigate to="/marketplace/login?next=/marketplace/messages" replace />;
  }

  return (
    <main className="max-w-6xl mx-auto px-6 py-8">
      <h1 className="text-3xl font-bold text-dark mb-6">Mensaxes</h1>
      <div className="grid md:grid-cols-[340px_1fr] gap-6">
        <aside className="border border-border rounded-2xl bg-white overflow-hidden">
          {items === null ? (
            <div className="p-6 flex items-center gap-2 text-sm text-muted">
              <Spinner size={16} /> Cargando…
            </div>
          ) : error ? (
            <div className="p-4 text-sm text-danger">{error}</div>
          ) : items.length === 0 ? (
            <div className="p-6 text-center">
              <MessagesSquare size={28} className="mx-auto text-muted mb-2" />
              <p className="text-sm font-semibold text-dark">Aínda non tes mensaxes</p>
              <p className="text-xs text-muted mt-1">
                Cando un comprador escriba sobre un animal teu, aparecerá aquí.
              </p>
              <Link
                to="/marketplace"
                className="inline-block mt-3 text-xs text-rauxa font-semibold hover:underline"
              >
                Explorar catálogo →
              </Link>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {items.map((c) => (
                <li key={c.id}>
                  <NavLink
                    to={`/marketplace/messages/${c.id}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 p-4 hover:bg-subtle transition-colors ${
                        isActive ? 'bg-subtle' : ''
                      }`
                    }
                  >
                    <Avatar user={c.counterparty} size={40} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold text-dark truncate">
                          {c.counterparty.name}
                        </span>
                        {c.unread_count > 0 && (
                          <span className="text-[10px] font-bold text-white bg-rauxa rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                            {c.unread_count}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted truncate">
                        {c.sale.animal_name ?? c.sale.animal_crotal}
                      </p>
                      {c.last_message && (
                        <p className="text-xs text-muted truncate mt-0.5">{c.last_message.text}</p>
                      )}
                    </div>
                  </NavLink>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <section className="min-h-[480px]">
          {params.id ? (
            <Outlet />
          ) : (
            <div className="border border-border rounded-2xl bg-white h-full flex items-center justify-center p-10 text-center">
              <div>
                <MessagesSquare size={32} className="mx-auto text-muted mb-2" />
                <p className="text-sm text-muted">Selecciona unha conversa á esquerda.</p>
              </div>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
